/**
 * Mini-roteador por hash (sem dependência): "#/caminho/para" → { rota, partes }.
 * Rotas por hash evitam qualquer fallback no servidor estático.
 */
import { useEffect, useMemo, useState } from "react";

export function useRota(): string[] {
  const [hash, setHash] = useState(() => window.location.hash);
  useEffect(() => {
    const ouvir = () => setHash(window.location.hash);
    window.addEventListener("hashchange", ouvir);
    return () => window.removeEventListener("hashchange", ouvir);
  }, []);
  const limpo = hash.replace(/^#\/?/, "");
  // a query viaja colada no último segmento ("ferias?novo=1") — o matching de
  // rota usa os segmentos SEM a query (useQueryRota expõe os parâmetros)
  return limpo === "" ? [] : limpo.split("/").map((p) => p.split("?")[0]!);
}

/** Query string da rota por hash ("#/ferias?novo=1&dias=15" → { novo: "1",
 *  dias: "15" }) — M1.8: deep-link do recibo pré-preenchido pelas datas do
 *  ponto. Reage a mudanças de hash (usa useRota como gatilho). */
export function useQueryRota(): URLSearchParams {
  const partes = useRota(); // gatilho de re-render na troca de hash
  return useMemo(() => {
    void partes;
    const bruto = window.location.hash.replace(/^#\/?/, "");
    const q = bruto.indexOf("?");
    return new URLSearchParams(q >= 0 ? bruto.slice(q + 1) : "");
  }, [partes]);
}

export function navegar(para: string): void {
  window.location.hash = `#${para}`;
}

/** 05/09/2026 (pedido do dono) — origem do deep-link: "/competencias/AAAAAA"
 *  validado (só esse formato navega — nada de hash arbitrário). Usado pelos
 *  deep-links da competência (férias) para oferecer "Voltar para a
 *  competência MM/AAAA" na página de destino. */
export function origemCompetencia(query: URLSearchParams): string | null {
  const o = query.get("origem");
  return o && /^\/competencias\/\d{6}$/.test(o) ? o : null;
}

/** Rótulo do botão de volta: "Voltar para a competência MM/AAAA". */
export function rotuloVoltarCompetencia(origem: string): string {
  const comp = Number(origem.split("/")[2]);
  const mes = String(comp % 100).padStart(2, "0");
  const ano = Math.floor(comp / 100);
  return `Voltar para a competência ${mes}/${ano}`;
}
